import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, FormControlLabel, Checkbox, Link,
  Divider, IconButton, Tooltip, Alert,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import api from '../../../api/axiosInterceptor';
import { SnackBarComponent } from '../../../Components/snackBar/snackBar';

const MAX_CLOSURE_FILE_SIZE = 2 * 1024 * 1024; // 2 MB, matches backend limit

// Moved out of DocsEditModal — these belong to post-payment closure, not
// the pre-decision document set.
const ClosureStatusModal = ({ open, onClose, excessId, refId, readOnly = false }) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const [orderStatus, setOrderStatus] = useState({
    is_partial_un_freezing_order_submited: false,
    partial_unfreeze_order_link: '',
    is_partial_un_freezed: false,
    is_defreezing_order_submited: false,
    defreezing_order_link: '',
    is_bank_defreezed: false,
  });

  const [files, setFiles] = useState({});
  const [caseClosed, setCaseClosed] = useState(false);

  const showError = (message) => {
    setSnackbarMessage(message);
    setSnackbarSeverity('error');
    setSnackbarOpen(true);
  };

  const fetchData = async () => {
    if (!excessId) return;
    try {
      setLoading(true);
      const res = await api.get(`/excess_payment_mgmt/excess-order-updater/${excessId}/`);
      setOrderStatus((prev) => ({ ...prev, ...res.data }));
      setFiles({});
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to load closure status.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchData();
  }, [open, excessId]);

  const handleCheckbox = (name) => (e) => {
    if (readOnly) return;
    const checked = e.target.checked;

    if (name === 'is_partial_un_freezing_order_submited' && !checked && orderStatus.is_partial_un_freezed) {
      showError('Please uncheck "Partially Unfreezed" before unchecking "Partial Unfreeze Order Submitted".');
      return;
    }
    if (name === 'is_partial_un_freezed' && !checked && orderStatus.is_defreezing_order_submited) {
      showError('Please uncheck "Defreezing Order Submitted" before unchecking "Partially Unfreezed".');
      return;
    }
    if (name === 'is_defreezing_order_submited' && !checked && orderStatus.is_bank_defreezed) {
      showError('Please uncheck "Bank Defreezed" before unchecking "Defreezing Order Submitted".');
      return;
    }

    setOrderStatus((prev) => ({ ...prev, [name]: checked }));
  };

  const handleFileChange = (fileFieldName, boolField) => (e) => {
    if (readOnly) return;
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_CLOSURE_FILE_SIZE) {
      showError('File must be under 2 MB.');
      return;
    }
    setFiles((prev) => ({ ...prev, [fileFieldName]: file }));
    setOrderStatus((prev) => ({ ...prev, [boolField]: true }));
  };

  const handleSubmit = async () => {
    if (readOnly) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('is_partial_un_freezing_order_submited', orderStatus.is_partial_un_freezing_order_submited);
      fd.append('is_partial_un_freezed', orderStatus.is_partial_un_freezed);
      fd.append('is_defreezing_order_submited', orderStatus.is_defreezing_order_submited);
      fd.append('is_bank_defreezed', orderStatus.is_bank_defreezed);
      if (files.partial_unfreeze_order_file) fd.append('partial_unfreeze_order_file', files.partial_unfreeze_order_file);
      if (files.defreezing_order_file) fd.append('defreezing_order_file', files.defreezing_order_file);

      const res = await api.patch(
        `/excess_payment_mgmt/excess-order-updater/${excessId}/`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (res.data?.case_closed) {
        setCaseClosed(true);
        setSnackbarMessage('Bank Defreezed confirmed — case has been closed.');
      } else {
        setSnackbarMessage('Closure status updated.');
      }
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      await fetchData();
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to update closure status.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth disableEscapeKeyDown>
      <DialogTitle>
        Closure — Partial Unfreeze &amp; Final Defreeze
        {readOnly && (
          <Typography variant="caption" color="text.secondary" display="block">
            View only — {refId}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {caseClosed && (
          <Alert severity="success" sx={{ mb: 2 }}>
            This case is now fully closed.
          </Alert>
        )}

        <Typography variant="subtitle2" gutterBottom>Partial Unfreeze</Typography>

        <Box display="flex" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
          <FormControlLabel
            sx={{ minWidth: 260 }}
            control={
              <Checkbox
                checked={orderStatus.is_partial_un_freezing_order_submited}
                onChange={handleCheckbox('is_partial_un_freezing_order_submited')}
                disabled={readOnly}
              />
            }
            label="Partial Unfreeze Order Submitted"
          />
          <Tooltip title={readOnly ? '' : 'Upload file (max 2 MB)'}>
            <span>
              <Button
                size="small"
                variant="outlined"
                component="label"
                startIcon={<UploadFileIcon fontSize="small" />}
                disabled={readOnly}
              >
                {files.partial_unfreeze_order_file ? files.partial_unfreeze_order_file.name : 'Upload'}
                <input
                  type="file"
                  hidden
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileChange('partial_unfreeze_order_file', 'is_partial_un_freezing_order_submited')}
                />
              </Button>
            </span>
          </Tooltip>
          {orderStatus.partial_unfreeze_order_link && (
            <Tooltip title="View uploaded file">
              <IconButton size="small" component={Link} href={orderStatus.partial_unfreeze_order_link} target="_blank" rel="noopener">
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Typography variant="body2" sx={{ mb: 1.5 }}>
          Template:{' '}
          <Link href="https://splocamedia.s3.ap-south-1.amazonaws.com/templates/EPMS_document/PARTIALLY+UN_FREEZE+THE+BANK+ACCOUNT.pdf" target="_blank">
            Click here
          </Link>
        </Typography>

        <FormControlLabel
          control={
            <Checkbox
              checked={orderStatus.is_partial_un_freezed}
              onChange={handleCheckbox('is_partial_un_freezed')}
              disabled={readOnly || !orderStatus.is_partial_un_freezing_order_submited}
            />
          }
          label="Partially Unfreezed"
        />

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" gutterBottom>Final Defreeze</Typography>

        <Box display="flex" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
          <FormControlLabel
            sx={{ minWidth: 260 }}
            control={
              <Checkbox
                checked={orderStatus.is_defreezing_order_submited}
                onChange={handleCheckbox('is_defreezing_order_submited')}
                disabled={readOnly || !orderStatus.is_partial_un_freezed}
              />
            }
            label="Defreezing Order Submitted"
          />
          <Tooltip title={readOnly ? '' : 'Upload file (max 2 MB)'}>
            <span>
              <Button
                size="small"
                variant="outlined"
                component="label"
                startIcon={<UploadFileIcon fontSize="small" />}
                disabled={readOnly || !orderStatus.is_partial_un_freezed}
              >
                {files.defreezing_order_file ? files.defreezing_order_file.name : 'Upload'}
                <input
                  type="file"
                  hidden
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileChange('defreezing_order_file', 'is_defreezing_order_submited')}
                />
              </Button>
            </span>
          </Tooltip>
          {orderStatus.defreezing_order_link && (
            <Tooltip title="View uploaded file">
              <IconButton size="small" component={Link} href={orderStatus.defreezing_order_link} target="_blank" rel="noopener">
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Typography variant="body2" sx={{ mb: 1.5 }}>
          Template:{' '}
          <Link href="https://splocamedia.s3.ap-south-1.amazonaws.com/templates/EPMS_document/UN_FREEZE+THE+BANK+ACCOUNT_.pdf" target="_blank">
            Click here
          </Link>
        </Typography>

        <FormControlLabel
          control={
            <Checkbox
              checked={orderStatus.is_bank_defreezed}
              onChange={handleCheckbox('is_bank_defreezed')}
              disabled={readOnly || !orderStatus.is_defreezing_order_submited}
            />
          }
          label="Bank Defreezed"
        />

        {!readOnly && orderStatus.is_bank_defreezed && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Saving with "Bank Defreezed" checked will close this case permanently.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {!readOnly && (
          <Button onClick={handleSubmit} variant="contained" disabled={loading || submitting}>
            {submitting ? 'Saving...' : 'Save'}
          </Button>
        )}
      </DialogActions>

      <SnackBarComponent
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={() => setSnackbarOpen(false)}
      />
    </Dialog>
  );
};

export default ClosureStatusModal;